import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import RFIDTagModal from './RFIDTagModal';

interface RFIDTag {
  _id: string;
  tagId: string;
  type: 'personal' | 'public';
  userId?: {
    _id: string;
    name: string;
    email: string;
  };
  status: string;
  expiryDate: string;
  description?: string;
  lastUsed?: string;
}

const RFIDTagList: React.FC = () => {
  const { t } = useTranslation();
  const [tags, setTags] = useState<RFIDTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<RFIDTag | null>(null);

  const fetchTags = async () => {
    try {
      const response = await api.get('/rfid-tags');
      setTags(response.data);
      setLoading(false);
    } catch (err) {
      setError(t('rfidTags.messages.fetchError'));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const handleStatusChange = async (tagId: string, newStatus: 'active' | 'blocked' | 'expired') => {
    try {
      await api.put(`/rfid-tags/${tagId}`, { status: newStatus });
      fetchTags();
    } catch (err) {
      setError(t('rfidTags.messages.updateError'));
    }
  };

  const handleAddClick = () => {
    setSelectedTag(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (tag: RFIDTag) => {
    setSelectedTag(tag);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTag(null);
  };

  const handleModalSave = () => {
    fetchTags();
    setIsModalOpen(false);
    setSelectedTag(null);
  };

  if (loading) return <div>{t('common.loading')}</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">{t('rfidTags.title')}</h2>
        <button
          onClick={handleAddClick}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          {t('rfidTags.addTag')}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead>
            <tr>
              <th className="border px-4 py-2">{t('rfidTags.tagId')}</th>
              <th className="border px-4 py-2">{t('rfidTags.type')}</th>
              <th className="border px-4 py-2">{t('rfidTags.user')}</th>
              <th className="border px-4 py-2">{t('rfidTags.status')}</th>
              <th className="border px-4 py-2">{t('rfidTags.expiryDate')}</th>
              <th className="border px-4 py-2">{t('rfidTags.lastUsed')}</th>
              <th className="border px-4 py-2">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {tags.map((tag) => (
              <tr key={tag._id}>
                <td className="border px-4 py-2">{tag.tagId}</td>
                <td className="border px-4 py-2">{t(`rfidTags.types.${tag.type}`)}</td>
                <td className="border px-4 py-2">
                  {tag.userId ? `${tag.userId.name} (${tag.userId.email})` : t('rfidTags.unassigned')}
                </td>
                <td className="border px-4 py-2">
                  <span className={`px-2 py-1 rounded ${
                    tag.status === 'active' ? 'bg-green-100 text-green-800' :
                    tag.status === 'blocked' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {t(`rfidTags.tagStatus.${tag.status}`)}
                  </span>
                </td>
                <td className="border px-4 py-2">
                  {new Date(tag.expiryDate).toLocaleDateString()}
                </td>
                <td className="border px-4 py-2">
                  {tag.lastUsed ? new Date(tag.lastUsed).toLocaleString() : t('rfidTags.never')}
                </td>
                <td className="border px-4 py-2">
                  <button
                    onClick={() => handleEditClick(tag)}
                    className="text-blue-600 hover:text-blue-800 mr-2"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={() => handleStatusChange(tag._id, 
                      tag.status === 'active' ? 'blocked' : 'active')}
                    className={`${
                      tag.status === 'active' ? 'text-red-600 hover:text-red-800' :
                      'text-green-600 hover:text-green-800'
                    }`}
                  >
                    {tag.status === 'active' ? t('rfidTags.actions.block') : t('rfidTags.actions.activate')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <RFIDTagModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        tag={selectedTag}
      />
    </div>
  );
};

export default RFIDTagList; 